import { Injectable } from '@angular/core'
import { DefaultPlatform } from './default'
import { TizenPlatform } from './tizen'
import { HTVPlatform, IHTVPlatform } from './platform'
import { LocalChannel } from '../interfaces/local-channel'
import { ChannelPosition } from '../pages/channel/channel-position'
import { MonitoringMemoryData, MonitoringStorageData, SystemData } from '../interfaces/monitoring'
import { LocalMediaFile, LogService, Media } from 'htv-lib'
import { TizenHBrowserApiPlatform } from './tizen-hbrowser'
import { LocalAppLaunchMode, TVApp } from '../interfaces/local-app'
import { ChannelTrackData, CurrentChannelData } from '../interfaces/channel-track-data'
import { PowerState } from '../interfaces/power-state'
import { BehaviorSubject } from 'rxjs'
import { NetworkData, NetworkState } from '../interfaces/network'
import { WebOSPlatform } from './webos'
import { StatusSuccessDto } from '../interfaces/dto/status-response-dto'
import { RouterService } from '../services/router.service'
import { PhilipsPlatform } from './philips'
import { GoogleCastState } from '../interfaces/local-googlecast'
import { ExternalInputData } from '../services/external-input.service'
import { VODVideo } from '../interfaces/vod'

@Injectable({
  providedIn: 'root',
})
export class PlatformService implements IHTVPlatform {
  type!: HTVPlatform
  platform!: IHTVPlatform

  private userAgent: string = window.navigator.userAgent

  constructor(
    private logService: LogService,
    private routerService: RouterService
  ) {
    this.logService.info(`User Agent: ${this.userAgent}`)

    if (this.platform != undefined) return

    // Tizen 3.0
    if (this.userAgent.indexOf('(SMART-TV; LINUX; Tizen 3.0') != -1) {
      this.logService.info('Tizen 3.0 detected, using H.Browser API')
      this.type = HTVPlatform.TIZEN
      this.platform = new TizenHBrowserApiPlatform(this.logService)
      return
    }

    // Tizen 4.0
    if (this.userAgent.indexOf('(SMART-TV; LINUX; Tizen 4.0') != -1) {
      this.logService.info('Tizen 4.0 detected, using H.Browser API')
      this.type = HTVPlatform.TIZEN
      this.platform = new TizenHBrowserApiPlatform(this.logService)
      return
    }

    // Tizen 6.0
    if (this.userAgent.indexOf('(SMART-TV; LINUX; Tizen 6.0') != -1) {
      this.logService.info('Tizen 6.0 detected, using H.Browser API')
      this.type = HTVPlatform.TIZEN
      this.platform = new TizenHBrowserApiPlatform(this.logService)
      return
    }

    // Tizen 6.5
    if (this.userAgent.indexOf('(SMART-TV; LINUX; Tizen 6.5') != -1) {
      this.logService.info('Tizen 6.5 detected, using Tizen TEP API')
      this.type = HTVPlatform.TIZEN
      this.platform = new TizenPlatform(this.logService, this.routerService)
      return
    }

    // Tizen 9.0
    if (this.userAgent.indexOf('(SMART-TV; LINUX; Tizen 9.0') != -1) {
      this.logService.info('Tizen 9.0 detected, using Tizen TEP API')
      this.type = HTVPlatform.TIZEN
      this.platform = new TizenPlatform(this.logService, this.routerService)
      return
    }

    // Tizen
    if (this.userAgent.indexOf('(SMART-TV; LINUX; Tizen') != -1) {
      this.logService.info('Tizen detected but is not supported, using Tizen API')
      this.type = HTVPlatform.TIZEN
      this.platform = new TizenPlatform(this.logService, this.routerService)
      return
    }

    // Philips
    if (this.userAgent.indexOf('(Linux; Android ') != -1) {
      this.logService.info('Philips detected, using Philips API')
      this.type = HTVPlatform.PHILIPS
      this.platform = new PhilipsPlatform(this.logService, this.routerService)
      return
    }

    // WebOS
    if (
      this.userAgent.indexOf('(webOS; Linux/SmartTV)') != -1 ||
      this.userAgent.indexOf('(WebOS; Linux/SmartTV)') != -1 ||
      this.userAgent.indexOf('(Web0S; Linux/SmartTV)') != -1
    ) {
      this.logService.info('WebOS detected, using WebOS API')
      this.type = HTVPlatform.WEBOS
      this.platform = new WebOSPlatform(this.logService, this.routerService)
      return
    }

    // Default
    this.logService.info('Nothing detected, using default API')
    this.type = HTVPlatform.DEFAULT
    this.platform = new DefaultPlatform(this.logService, this.routerService)
  }

  id(): string {
    return this.platform.id()
  }

  async init(): Promise<void> {
    this.logService.info(`[PlatformService] Init`)
    return await this.platform.init()
  }

  afterRegistrationHook(deviceId: string | undefined): void {
    return this.platform.afterRegistrationHook(deviceId)
  }

  afterStatusHook(status: StatusSuccessDto): Promise<void> {
    return this.platform.afterStatusHook(status)
  }

  enableGuestMode(): Promise<void> {
    return this.platform.enableGuestMode()
  }

  disableGuestMode(): Promise<void> {
    return this.platform.disableGuestMode()
  }

  clearData(): Promise<void> {
    return this.platform.clearData()
  }

  getSystemData(): Promise<SystemData> {
    this.logService.debug(`PlatformService.getSystemData()`)
    return this.platform.getSystemData()
  }

  getPlatformData(): Promise<object> {
    return this.platform.getPlatformData()
  }

  getCpuUsage(): Promise<number> {
    return this.platform.getCpuUsage()
  }

  getMemoryData(): Promise<MonitoringMemoryData> {
    return this.platform.getMemoryData()
  }

  getStorageData(): Promise<MonitoringStorageData[]> {
    return this.platform.getStorageData()
  }

  getTemperature(): Promise<number> {
    return this.platform.getTemperature()
  }

  getValue(key: string): any {
    return this.platform.getValue(key)
  }

  setValue(key: string, value: any): void {
    this.platform.setValue(key, value)
  }

  removeValue(key: string): void {
    this.platform.removeValue(key)
  }

  clearValues(): void {
    this.platform.clearValues()
  }

  reboot(): void {
    this.logService.debug(`PlatformService.reboot()`)
    this.platform.reboot()
  }

  setLanguage(code: string): Promise<void> {
    return this.platform.setLanguage(code)
  }

  getPowerState(): Promise<PowerState | undefined> {
    return this.platform.getPowerState()
  }

  setPowerState(state: PowerState): void {
    this.logService.debug(`PlatformService.setPowerState(${state})`)
    this.platform.setPowerState(state)
  }

  registerPowerStateListener(subject: BehaviorSubject<PowerState | undefined>): void {
    this.logService.debug(`PlatformService.registerPowerStateListener(...)`)
    this.platform.registerPowerStateListener(subject)
  }

  getNetworkState(): Promise<NetworkState> {
    return this.platform.getNetworkState()
  }

  getNetworkData(): Promise<NetworkData> {
    return this.platform.getNetworkData()
  }

  registerNetworkStateListener(subject: BehaviorSubject<NetworkState>): void {
    this.logService.debug(`PlatformService.registerNetworkStateListener(...)`)
    this.platform.registerNetworkStateListener(subject)
  }

  registerApplicationInstalledListener(subject: BehaviorSubject<string[]>): void {
    this.platform.registerApplicationInstalledListener(subject)
  }

  registerApplicationFocusListener(subject: BehaviorSubject<boolean>): void {
    this.platform.registerApplicationFocusListener(subject)
  }

  getAppList(): Promise<TVApp[]> {
    this.logService.debug(`PlatformService.getAppList()`)
    return this.platform.getAppList()
  }

  startApp(appId: string): void {
    this.platform.startApp(appId)
  }

  startNetflixApp(appId: string, hotelId: string, launchMode: LocalAppLaunchMode): void {
    this.logService.debug(`PlatformService.startNetflixApp(${appId}, ${hotelId}, ${launchMode})`)
    this.platform.startNetflixApp(appId, hotelId, launchMode)
  }

  registerChannelListener(subject: BehaviorSubject<boolean>): void {
    this.platform.registerChannelListener(subject)
  }

  startUDPChannel(channel: LocalChannel, pos: ChannelPosition): void {
    this.platform.startUDPChannel(channel, pos)
  }

  startWebChannel(channel: LocalChannel, pos: ChannelPosition): void {
    this.platform.startWebChannel(channel, pos)
  }

  startBroadcastChannel(channel: LocalChannel, pos: ChannelPosition): void {
    this.platform.startBroadcastChannel(channel, pos)
  }

  stopUDPChannel(): void {
    this.platform.stopUDPChannel()
  }

  stopWebChannel(): void {
    this.platform.stopWebChannel()
  }

  stopBroadcastChannel(): void {
    this.platform.stopBroadcastChannel()
  }

  getCurrentChannel(): Promise<any> {
    return this.platform.getCurrentChannel()
  }

  getUDPCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    return this.platform.getUDPCurrentProgramData()
  }

  getWebCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    return this.platform.getWebCurrentProgramData()
  }

  getBroadcastCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    return this.platform.getBroadcastCurrentProgramData()
  }

  setUDPAudioTrack(track: ChannelTrackData): void {
    this.logService.debug(`PlatformService.setUDPAudioTrack(${track.lang}(${track.index}))`)
    this.platform.setUDPAudioTrack(track)
  }

  setWebAudioTrack(track: ChannelTrackData): void {
    this.logService.debug(`PlatformService.setWebAudioTrack(${track.lang}(${track.index}))`)
    this.platform.setWebAudioTrack(track)
  }

  setBroadcastAudioTrack(track: ChannelTrackData): void {
    this.logService.debug(`PlatformService.setBroadcastAudioTrack(${track.lang}(${track.index}))`)
    this.platform.setBroadcastAudioTrack(track)
  }

  setUDPSubtitleTrack(track: ChannelTrackData): void {
    this.logService.debug(`PlatformService.setUDPSubtitleTrack(${track.lang}(${track.index}))`)
    this.platform.setUDPSubtitleTrack(track)
  }

  setWebSubtitleTrack(track: ChannelTrackData): void {
    this.logService.debug(`PlatformService.setWebSubtitleTrack(${track.lang}(${track.index}))`)
    this.platform.setWebSubtitleTrack(track)
  }

  setBroadcastSubtitleTrack(track: ChannelTrackData): void {
    this.logService.debug(`PlatformService.setBroadcastSubtitleTrack(${track.lang}(${track.index}))`)
    this.platform.setBroadcastSubtitleTrack(track)
  }

  setSubtitleState(enable: boolean): Promise<boolean> {
    this.logService.debug(`PlatformService.setSubtitleState(${enable}})`)
    return this.platform.setSubtitleState(enable)
  }

  getLocalMedias(): Promise<LocalMediaFile[]> {
    this.logService.debug(`PlatformService.getLocalMedias()`)
    return this.platform.getLocalMedias()
  }

  downloadMedia(media: Media): Promise<LocalMediaFile> {
    this.logService.debug(`PlatformService.downloadMedia(${media.id})`)
    return this.platform.downloadMedia(media)
  }

  removeMedia(mediaFile: LocalMediaFile): Promise<void> {
    this.logService.debug(`PlatformService.removeMedia(${mediaFile.id})`)
    return this.platform.removeMedia(mediaFile)
  }

  setNativeVolumeDisplay(enabled: boolean): void {
    this.platform.setNativeVolumeDisplay(enabled)
  }

  getVolume(): Promise<number> {
    return this.platform.getVolume()
  }

  setVolume(volume: number): Promise<number> {
    this.logService.debug(`PlatformService.setVolume(${volume})`)
    return this.platform.setVolume(volume)
  }

  setVolumeDown(): Promise<number> {
    this.logService.debug(`PlatformService.setVolumeDown()`)
    return this.platform.setVolumeDown()
  }

  setVolumeUp(): Promise<number> {
    this.logService.debug(`PlatformService.setVolumeUp()`)
    return this.platform.setVolumeUp()
  }

  getVolumeMute(): Promise<boolean> {
    return this.platform.getVolumeMute()
  }

  setVolumeMute(muted: boolean): Promise<boolean> {
    this.logService.debug(`PlatformService.setVolumeMute(${muted})`)
    return this.platform.setVolumeMute(muted)
  }

  getExternalInputs(): Promise<ExternalInputData[]> {
    return this.platform.getExternalInputs()
  }

  registerExternalInputListener(subject: BehaviorSubject<ExternalInputData[]>): void {
    this.platform.registerExternalInputListener(subject)
  }

  showHDMI(externalInput: ExternalInputData): Promise<void> {
    this.logService.debug(`PlatformService.showHDMI(${externalInput.id})`)
    return this.platform.showHDMI(externalInput)
  }

  hideHDMI(): Promise<void> {
    this.logService.debug(`PlatformService.hideHDMI()`)
    return this.platform.hideHDMI()
  }

  hasIntegratedGoogleCast(): boolean {
    return this.platform.hasIntegratedGoogleCast()
  }

  initGoogleCastApp(): void {
    this.platform.initGoogleCastApp()
  }

  registerGoogleCastInitializedListener(subject: BehaviorSubject<boolean | undefined>): void {
    this.platform.registerGoogleCastInitializedListener(subject)
  }

  disableGoogleCast(): Promise<void> {
    return this.platform.disableGoogleCast()
  }

  initGoogleCastProxy(): Promise<void> {
    return this.platform.initGoogleCastProxy()
  }

  initGoogleCastSoftAP(): Promise<void> {
    return this.platform.initGoogleCastSoftAP()
  }

  acceptGoogleCastTermsOfService(): Promise<void> {
    return this.platform.acceptGoogleCastTermsOfService()
  }

  enableGoogleCastUsageReporting(): Promise<void> {
    return this.platform.enableGoogleCastUsageReporting()
  }

  clearGoogleCastData(): Promise<void> {
    return this.platform.clearGoogleCastData()
  }

  openGoogleCastLocalPage(
    roomNumber: string,
    supportMessage: string,
    gcStateSubject: BehaviorSubject<GoogleCastState | undefined>
  ): Promise<void> {
    return this.platform.openGoogleCastLocalPage(roomNumber, supportMessage, gcStateSubject)
  }

  openGoogleCastSoftAPPage(roomNumber: string, pairingStatus: boolean, supportMessage: string): Promise<void> {
    return this.platform.openGoogleCastSoftAPPage(roomNumber, pairingStatus, supportMessage)
  }

  pollGoogleCastSoftAP(): Promise<any> {
    return this.platform.pollGoogleCastSoftAP()
  }

  launchGoogleCastSettings(): Promise<void> {
    return this.platform.launchGoogleCastSettings()
  }

  adminOpenAppsSettings(): void {
    this.platform.adminOpenAppsSettings()
  }

  downloadAndUpgradeApp(deviceId: string): void {
    this.platform.downloadAndUpgradeApp(deviceId)
  }

  canDownloadAndUpgradeApp(): boolean {
    return this.platform.canDownloadAndUpgradeApp()
  }

  hasCustomVOD(): boolean {
    return this.platform.hasCustomVOD()
  }

  getShakaPlatformConfig(): any {
    return this.platform.getShakaPlatformConfig()
  }

  vodStart(vod: VODVideo, pos: ChannelPosition): void {
    this.platform.vodStart(vod, pos)
  }

  vodPlay(vod: VODVideo): void {
    this.platform.vodPlay(vod)
  }

  vodPause(vod: VODVideo): void {
    this.platform.vodPause(vod)
  }

  vodStop(): void {
    this.platform.vodStop()
  }

  vodGetCurrentTime(): number {
    return this.platform.vodGetCurrentTime()
  }

  vodSpeed(vod: VODVideo, speed: number): void {
    this.platform.vodSpeed(vod, speed)
  }

  vodSeekTo(vod: VODVideo, milliseconds: number): Promise<void> {
    return this.platform.vodSeekTo(vod, milliseconds)
  }

  vodJumpBackward(vod: VODVideo, milliseconds: number): Promise<void> {
    return this.platform.vodJumpBackward(vod, milliseconds)
  }

  vodJumpForward(vod: VODVideo, milliseconds: number): Promise<void> {
    return this.platform.vodJumpForward(vod, milliseconds)
  }

  initVirtualKeyboard(): void {
    return this.platform.initVirtualKeyboard()
  }
}
